import React from "react";
import { flushSync } from "react-dom";
import { gsap } from "gsap";
const ANIMATED_SELECTORS = ["[data-animate-title]", "[data-animate-illustration]", "[data-animate-spec-row]", "[data-animate-spec-line]", "[data-animate-spec-label]", "[data-animate-spec-value]", "[data-animate-link]", "[data-animate-support]", "[data-animate-download]"].join(", ");
export function useAnimation() {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const splitInstancesRef = React.useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SplitTextRef = React.useRef<any>(null);
  const activeIndexRef = React.useRef(0);
  const animVersionRef = React.useRef(0);
  const hasInitializedRef = React.useRef(false);
  React.useEffect(() => {
    if (typeof window !== "undefined" && !SplitTextRef.current) {
      import("gsap/SplitText").then(module => {
        SplitTextRef.current = module.SplitText;
        gsap.registerPlugin(module.SplitText);
      });
    }
  }, []);
  const animateIn = React.useCallback((container: HTMLDivElement) => {
    const SplitText = SplitTextRef.current;
    splitInstancesRef.current.forEach(s => s.revert());
    splitInstancesRef.current = [];
    gsap.set(container.querySelectorAll(ANIMATED_SELECTORS), {
      clearProps: "all"
    });
    const tl = gsap.timeline({
      defaults: {
        ease: "cubic-bezier(0.125, 0.425, 0.27, 1)"
      }
    });
    timelineRef.current = tl;
    const illustration = container.querySelector("[data-animate-illustration]");
    const title = container.querySelector("[data-animate-title]");
    const specLines = container.querySelectorAll("[data-animate-spec-line]");
    const specLabels = container.querySelectorAll("[data-animate-spec-label]");
    const specValues = container.querySelectorAll("[data-animate-spec-value]");
    const actionLinks = container.querySelectorAll("[data-animate-link], [data-animate-support], [data-animate-download]");
    if (title) {
      tl.fromTo(title, {
        opacity: 0,
        x: -10
      }, {
        opacity: 1,
        x: 0,
        duration: 0.5
      }, 0);
    }
    if (illustration) {
      tl.fromTo(illustration, {
        opacity: 0,
        scale: 0.95
      }, {
        opacity: 1,
        scale: 1,
        duration: 0.7
      }, 0.1);
    }
    if (specLines.length > 0) {
      tl.fromTo(specLines, {
        scaleX: 0,
        transformOrigin: "left center"
      }, {
        scaleX: 1,
        duration: 0.5,
        stagger: 0.08
      }, 0.15);
    }
    if (SplitText && specLabels.length > 0) {
      specLabels.forEach((label, i) => {
        const split = new SplitText(label, {
          type: "lines"
        });
        splitInstancesRef.current.push(split);
        tl.fromTo(split.lines, {
          y: "100%",
          opacity: 0
        }, {
          y: "0%",
          opacity: 1,
          duration: 0.5,
          stagger: 0.03
        }, 0.25 + i * 0.08);
      });
    } else if (specLabels.length > 0) {
      tl.fromTo(specLabels, {
        opacity: 0,
        y: 12
      }, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.06
      }, 0.25);
    }
    if (SplitText && specValues.length > 0) {
      specValues.forEach((value, i) => {
        const split = new SplitText(value, {
          type: "lines"
        });
        splitInstancesRef.current.push(split);
        tl.fromTo(split.lines, {
          y: "100%",
          opacity: 0
        }, {
          y: "0%",
          opacity: 1,
          duration: 0.5,
          stagger: 0.03
        }, 0.3 + i * 0.08);
      });
    } else if (specValues.length > 0) {
      tl.fromTo(specValues, {
        opacity: 0,
        y: 12
      }, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.06
      }, 0.3);
    }
    if (actionLinks.length > 0) {
      tl.fromTo(actionLinks, {
        opacity: 0,
        y: 10
      }, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.08
      }, 0.6);
    }
    tl.timeScale(2);
    return tl;
  }, []);
  const animateOut = React.useCallback((container: HTMLDivElement) => {
    const illustration = container.querySelector("[data-animate-illustration]");
    const title = container.querySelector("[data-animate-title]");
    const specRows = container.querySelectorAll("[data-animate-spec-row]");
    const actionLinks = container.querySelectorAll("[data-animate-link], [data-animate-support], [data-animate-download]");
    const elements = [title, illustration, ...Array.from(specRows), ...Array.from(actionLinks)].filter(Boolean);
    return gsap.timeline().to(elements, {
      opacity: 0,
      y: -10,
      duration: 0.25,
      stagger: 0.02,
      ease: "power2.in"
    }).timeScale(2);
  }, []);
  const changeSlide = React.useCallback((newIndex: number, setActiveIndex: React.Dispatch<React.SetStateAction<number>>) => {
    if (newIndex === activeIndexRef.current) return;
    const version = ++animVersionRef.current;
    const container = contentRef.current;
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
    if (!container) {
      activeIndexRef.current = newIndex;
      setActiveIndex(newIndex);
      return;
    }
    const exitTl = animateOut(container);
    exitTl.eventCallback("onComplete", () => {
      if (animVersionRef.current !== version) return;
      splitInstancesRef.current = [];
      activeIndexRef.current = newIndex;
      flushSync(() => {
        setActiveIndex(newIndex);
      });
      animateIn(container);
    });
  }, [animateIn, animateOut]);
  React.useEffect(() => {
    const container = contentRef.current;
    if (!container || hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    requestAnimationFrame(() => {
      animateIn(container);
    });
  }, [animateIn]);
  React.useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      splitInstancesRef.current.forEach(s => s.revert());
      splitInstancesRef.current = [];
    };
  }, []);
  return {
    contentRef,
    changeSlide
  };
}